import { retrieveTripType } from '@pcln/flodash'
import { v4 as uuidv4 } from 'uuid'

import { BUNDLE_REQUEST, REQUEST_LOWERBOUND, REQUEST_UPPERBOUND } from '../../../constants/constants'
// TODO: Move this to current folder
import { buildRequestSliceData, getSliceFilters } from '../../../util/requestModels/search-request-helper'

import { hideExpressDeals } from './helpers/hideExpressDeals'

import { getWhiteLabelPlf } from '../../../../../common/utils/getWhiteLabelInfo'
import { getPkgRguid } from '../../../../utils/getPkgRguid'
import { getItineraryType, getNumOfPassengers } from './helpers'

/**
 * Call this function only 1 time when Initial AirSearchRequest has to be built
 * */
export function createInitialSearchRequestObject({ pclnBootstrapData, airSearchRequestModel }) {
  const { searchRequest, hasUSDestinationsOnly, features = {}, requestLocals = {} } = pclnBootstrapData

  const { referral = {}, locale = {} } = requestLocals

  if (!searchRequest) {
    return
  }

  const { slices, params, airports } = searchRequest

  const { 'cabin-class': cabinClass } = params

  /**
   * TODO: This should be calculated only on the server and passed down to client
   * from pclnBootstrapData. Calculating in multiple places is a waste of resources
   * */
  const tripType = retrieveTripType(slices, 'originAirport', 'destinationAirport', airports)

  const itineraryType = getItineraryType(tripType)

  /**
   * ========== Passengers start ==========
   * For passengers, only count the number of adults, children
   * Exclude the infants
   * */
  const passengers = {
    adults: getNumOfPassengers(params['num-adults']) || 1,
    children: getNumOfPassengers(params['num-children']),
    infants: getNumOfPassengers(params['num-infants']),
  }

  // ========== Passengers End ==========

  let nearByOrigin = []
  let nearByDest = []

  if (searchRequest.params['near-by-origin']) {
    nearByOrigin = searchRequest.params['near-by-origin'].split('-')
  }

  if (searchRequest.params['near-by-dest']) {
    nearByDest = searchRequest.params['near-by-dest'].split('-')
  }

  // ========== Package/Bundle Pricing request START ==========
  const bundleUspCacheKey = searchRequest.params['usp-cache-key']
  const standaloneUspCacheKey = requestLocals.uspCacheKey
  const uspCacheKey = bundleUspCacheKey || standaloneUspCacheKey
  const flyComponent = pclnBootstrapData.bundleComponents?.find((component) => component?.type === 'FLY')
  if (flyComponent && uspCacheKey) {
    const { index, key } = flyComponent

    // Only for Packages Listings experience
    if (bundleUspCacheKey) {
      airSearchRequestModel.expressDeal = null
      airSearchRequestModel.requestType = BUNDLE_REQUEST
    }

    airSearchRequestModel.packageSearchQuery = {
      uspCacheKey,
      flyComponentIndex: index,
      flyComponentKey: key,
      enableBasicEconomyExclusion: false,
    }
  }

  // ========== Package/Bundle Pricing request END ==========

  // ========== Slices Start ==========
  const sliceData = buildRequestSliceData({
    slices,
    tripType,
    nearByOrigin,
    nearByDest,
    bundleLocationData: flyComponent?.query,
    AIR_RECO_SORT_BY_DISPLAY_WEB: pclnBootstrapData.isNotReshopAndWLAndPackages && tripType !== 'MD',
  })
  // ========== Slices End ==========

  // ========== Set Initial Filters Start ==========
  // TODO:  Refactor `getSliceFilters` after the win
  const newFilters = getSliceFilters({
    searchRequest,
    tripType,
  })
  // ========== Set Initial Filters End ==========

  /**
   * Populate the AirSearchRequest Model with initial values
   * */
  Object.assign(airSearchRequestModel, {
    itineraryType,
    passengers,
    slices: sliceData,
    isUSOnly: hasUSDestinationsOnly,
    appCode: pclnBootstrapData?.isMobile ? 'MOBILEWEB' : 'DESKTOP',
  })

  if (airSearchRequestModel.expressDeal) {
    airSearchRequestModel.expressDeal.includeFused = true
  }

  /**
   * Default value of 'ECO' is already set on the AirSearchRequestModel
   * */
  if (cabinClass) {
    airSearchRequestModel.searchOptions.cabinClass = cabinClass
  }

  // Populate filters
  airSearchRequestModel.filter.sliceFilters = newFilters

  // ========== Set Upsell Flag start ==========

  airSearchRequestModel.searchOptions.upsell =
    pclnBootstrapData?.isShelves ||
    (pclnBootstrapData?.isHorizonListing &&
      !pclnBootstrapData?.isPackageListings &&
      !pclnBootstrapData?.partner)

  // ========== Set Upsell Flag end ==========

  // Add Referral Object

  const { clickId, id, sourceId } = referral
  const referralObj = {
    external: {
      refClickId: clickId,
      refId: id,
      referralSourceId: sourceId,
    },
  }
  airSearchRequestModel.referrals.push(referralObj)
  airSearchRequestModel.clientCountryCode = locale?.countryCode ?? ''

  airSearchRequestModel.modelInitialized = true

  if (features?.whiteLabel?.flightsExpressDealsEnabled === false) {
    airSearchRequestModel.expressDeal = null
  }

  return airSearchRequestModel
}

/**
 * Call this function every time an Air Search Request has to be made (i.e. filter, slice selection, load more)
 * */
export function buildSearchRequest({
  fromFilters = false, // request originating from filters path
  fromSlice = false,
  pclnBootstrapData,
  isCacheSearch,
  sortOptions,
  AIR_ENABLE_CHILD_PRICING,
  enableLapInfants,
  airSearchRequestModel,
  filterDefaults,
  filterInfo,
  fromLoadMore = false, // request from loadmore path
  rguid,
  activeTabId,
  listingsMetaData,
  numOfRenderedListings,
  employeeDiscountEnabled = false, // NEW: Employee discount toggle state
  standardSessionKey = '', // NEW: Standard pricing session key
  employeeSessionKey = '', // NEW: Employee pricing session key
}) {
  const request = {
    ...airSearchRequestModel,
  }
  if (request.expressDeal) {
    request.expressDeal.includeFused = true
  }

  // Handle employee discount toggle
  if (employeeDiscountEnabled) {
    // Toggle ON: Use employee session key if available, otherwise get new one
    if (employeeSessionKey) {
      request.searchSessionKey = employeeSessionKey
    } else {
      // Remove session key to get new employee session
      delete request.searchSessionKey
    }

    // Add employeeDiscount to searchOptions
    request.searchOptions = {
      ...request.searchOptions,
      employeeDiscount: true,
    }
  } else {
    // Toggle OFF: Use standard session key if available, otherwise use current session key
    if (standardSessionKey) {
      request.searchSessionKey = standardSessionKey
    }

    // Remove employeeDiscount from searchOptions if it exists
    if (request.searchOptions?.employeeDiscount) {
      delete request.searchOptions.employeeDiscount
    }
  }

  const hideExd = hideExpressDeals({
    airSearchRequestModel,
    filterDefaults,
    sortOptions,
    fromSlice,
    activeTabId,
    AIR_RECO_SORT_BY_DISPLAY_WEB: pclnBootstrapData.isNotReshopAndWLAndPackages,
  })

  if (hideExd) {
    request.expressDeal = null
  }

  // Disable Express Deals when employee discount is enabled
  if (employeeDiscountEnabled) {
    request.expressDeal = null
  }

  if (pclnBootstrapData.partner) {
    const isPackage = typeof airSearchRequestModel?.packageSearchQuery !== 'undefined'
    request.plfCode = getWhiteLabelPlf(pclnBootstrapData.partner, isPackage)
  }

  if (pclnBootstrapData?.offerToken) {
    request.offerToken = pclnBootstrapData?.offerToken
  }

  request.expFlags = {
    AIR_ENABLE_CHILD_PRICING,
    enableLapInfants,
  }

  /**
   * Not needed in the API request
   * */
  delete request.modelInitialized
  const { itineraryType } = request
  const isMdFireFly = itineraryType === 'MULTI_DESTINATION'
  const isRoundTrip = itineraryType === 'ROUND_TRIP'

  request.requestId = rguid ? rguid : getPkgRguid() || uuidv4()

  request.clientSessionId = request.clientSessionId || uuidv4()

  //Unchecking filters on first request without filter interaction
  request.uncheckFilterInitialRequest = !fromFilters

  if (isCacheSearch) {
    request.cache = {
      isCacheSearch: true,
      ageOfCacheInMinutes: 1440, // MOVE TO Constants
    }
  }

  /**
   * For Round Trip, Multidestination, slice by slice
   * */
  if (isRoundTrip) {
    /**
     * When sliceKey is exsists, it has a previous slice
     */
    const filter = request.filter.sliceFilters.filter((filter) => filter.hasOwnProperty('sliceKey'))

    request.displayParameters.sliceRefId = [filter?.length > 0 ? filter?.[0]?.sliceId + 1 : 1]
  }

  if (isMdFireFly) {
    const isFirstSlice = !request?.displayParameters.sliceRefId?.[0]

    // Increment ActiveTabId when
    // 1. When first slice is loaded (initiation)
    // 2. Request is from a non-slice-filter (i.e. stops, airports)

    if (isFirstSlice || (!isFirstSlice && fromSlice)) {
      const prevActiveTabId = request.displayParameters.sliceRefId?.[0] || 0
      request.displayParameters.sliceRefId = [prevActiveTabId + 1]
    }
  }

  const sliceRefId = request.displayParameters?.sliceRefId?.[0] || 1

  /**
   * Send selectedFareBrand to fly-ql to calculate incremental pricing for:
   * - slice selection
   * - filter request
   * - view more request
   */
  request.filter.sliceFilters.forEach((filter) => {
    const selectedFareBrand = filterInfo?.sliceFilters?.find(
      (item) => item?.sliceId === filter?.sliceId
    )?.selectedFareBrand

    // For non-slice selection requests (filter/load more) on slice 1,
    // send the defaultPackagePrice to correctly calculate incremental pricing
    if (!fromSlice && sliceRefId === 1 && pclnBootstrapData?.isPackageListings) {
      filter.selectedFareBrand = {
        avgPrice: listingsMetaData?.defaultPackagePrice,
      }
    } else if (selectedFareBrand) {
      filter.selectedFareBrand = {
        name: selectedFareBrand?.name,
        avgPrice: selectedFareBrand?.avgPrice,
        selectedPriceIndex: selectedFareBrand?.selectedPriceIndex,
      }
    }
  })

  if (fromLoadMore) {
    request.filter.type = 'LOAD_MORE'
    request.searchOptions.numOfRenderedListings = numOfRenderedListings
  } else if (fromSlice) {
    request.filter.type = 'SLICE_SELECTION'
    request.searchOptions.numOfRenderedListings = null
  } else if (fromFilters) {
    request.filter.type = 'FILTER'
    request.searchOptions.numOfRenderedListings = null
  }

  if (itineraryType === 'ONE_WAY') {
    request.displayParameters.sliceRefId = [1]
  }

  // Reset LowerBound and upperBound on click of any filter
  if (fromFilters) {
    request.displayParameters.lowerBound = REQUEST_LOWERBOUND
    request.displayParameters.upperBound = REQUEST_UPPERBOUND
  }

  if (sortOptions) {
    request.slices[sliceRefId - 1].sortOptions[0] = sortOptions[sliceRefId]
  }

  return request
}
